import _, { delay } from "lodash";
import { useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import apiAgent from "../../api/apiAgent";
import { useNavigate, useParams } from "react-router";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { useAtomValue } from "jotai";
import { modelDataAtom } from "../../atoms";
import { BackButton, DeleteButton, EditButton } from "../../components/Button";
import { Link, useSearchParams } from "react-router-dom";
import { getDataAndSchemaFromModel } from "../../components/Forms/utils";
import { DisplayValue, UserNameDisplay } from "../../components/DisplayValue";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, mergeClass, pause } from "../../utils";
import { useSetChangesMade } from "../../components/Publishing/utils";
import {
  IField,
  ITemplate,
  screenTypesArray,
} from "../../utils/slideTemplateAllocation";
import { PreviewSelector } from "./previews";
import { ShowBanner } from "../../components/ShowBanner";
import { getBackgroundColorLabelFromString } from "./previews/utils";
import { DeletePopup } from "../../components/ConfirmationPopup";

const alwaysShownFields: IField[] = [
  { field: "updatedAt" },
  { field: "createdAt" },
];

const ShowSlides = (): JSX.Element => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const screenId = _.toNumber(searchParams.get("screenType") || -1);
  // const [originFrom, originId] = useOriginParams();
  const modelName = "slides";
  const setChanges = useSetChangesMade(modelName);

  const modelData = useAtomValue(modelDataAtom);
  const [schema, setSchema] = useState<any[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState<boolean>(true);
  const [itemData, setItemData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    if (modelData && modelData !== null && isLoadingSchema) {
      const [, getSchema] = getDataAndSchemaFromModel(
        modelData,
        modelName,
        false
      );

      // we don't set item data for update
      setSchema(getSchema);
      setIsLoadingSchema(false);
    }
  }, [modelData, modelName]);

  const fetchData = async () => {
    try {
      const response = await apiAgent.DataType.getAll(modelName);
      const { data } = response;
      const item =
        data.find((item: any) => _.toString(item.id) === params.id) || null;

      setItemData(item);
      if (item.screenType !== screenId) {
        tMsg.warning(
          "This item's screen ID does not match the current preview's screen ID."
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePressed = async () => {
    try {
      if (!params.id) throw "No ID specified.";
      const response = await apiAgent.DataType.delete(modelName, {
        targetIdx: _.toNumber(params.id),
      });
      if (response.status === 200) {
        tMsg.success(`Slide deleted`);
        setChanges();
        navigate(`/m/${modelName}?screenType=${screenId}`);
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting slide`);
      console.log(err);
    }
  };

  const handlePublishPressed = async () => {
    try {
      if (!params.id) throw "No ID specified";
      setIsPublishing(true);
      await pause(300);
      const time = new Date();
      const published = !itemData.published || false;
      const response = await apiAgent.DataType.update(modelName, {
        ...itemData,
        published,
        publishedAt: time,
        updatedAt: time,
        targetIdx: itemData.id,
        donorList: itemData.donorList?.map((d: string) =>
          d.replaceAll('"', "")
        ),
      });
      if (response.status === 200) {
        tMsg.success(
          published ? `Slide set to visible` : `Slide set to hidden`
        );
        fetchData();
      }
    } catch (err) {
      tMsg.error(`Error toggling visibility`);
      console.log(err);
    } finally {
      setIsPublishing(false);
    }
  };

  const screenType = screenTypesArray.find(
    (screen) => screen.id === itemData?.screenType
  );
  const templates: ITemplate[] = screenType?.data?.templateArray || [];
  const templateFields = templates[itemData?.templateType]?.fields
    ? templates[itemData?.templateType]?.fields
    : [];
  const allFields = [...templateFields, ...alwaysShownFields];
  // const displaySchema = schema.reduce((arr, field) => {
  //   if (_.includes(allFilters, field.name)) return [...arr, field];
  //   return arr;
  // }, []);
  const displaySchema = allFields.reduce((arr: any[], field: IField) => {
    const getSchemaField = schema.find((f) => f.name === field.field);
    if (getSchemaField)
      return [
        ...arr,
        {
          ...getSchemaField,
          label: field.display,
        },
      ];
    return arr;
  }, []);

  const screenTypeName = `${screenType?.name || "Unknown screen ID"}`;
  const crumbs = [
    {
      label: screenTypeName,
      link: `/m/${modelName}?screenType=${screenId}`,
    },
  ];

  return (
    <PageContainer
      isLoading={isLoading || isLoadingSchema}
      className="relative"
    >
      <PageContent reduceHeightForFooter>
        <BreadCrumbs crumbs={crumbs} />
        {
          // TODO: Create a better display if id/itemData is invalid
          params.id && itemData !== null ? (
            <>
              <TitleBar title={`${screenTypeName} slide`}>
                <BackButton to={`/m/${modelName}?screenType=${screenId}`} />
              </TitleBar>
              {itemData && (
                <ShowBanner
                  isPublishing={isPublishing}
                  handlePublishPressed={handlePublishPressed}
                  published={itemData.published}
                />
              )}
              <div className="flex flex-col gap-4">
                <UserNameDisplay id={itemData.editedBy} />
                {displaySchema.map((item: any, index: number) => {
                  const value =
                    item.name === "backgroundColor"
                      ? getBackgroundColorLabelFromString(
                          itemData.backgroundColor
                        )
                      : itemData[item.name];
                  return (
                    <DisplayValue
                      key={index}
                      name={item.name}
                      model={modelName}
                      value={value}
                      type={item.type}
                      label={item.label}
                    />
                  );
                })}
              </div>
              <PreviewSelector screenType={screenId} itemData={itemData} />
            </>
          ) : (
            <TitleBar title={`Item ${params.id} not found`} />
          )
        }
      </PageContent>
      <PageFooterBar>
        <Link to={`/m/${modelName}/edit/${params.id}?screenType=${screenId}`}>
          <EditButton />
        </Link>
        <DeleteButton onClick={() => setShowDeletePopup(true)} />
      </PageFooterBar>
      <DeletePopup
        setShowModal={setShowDeletePopup}
        showModal={showDeletePopup}
        confirmAction={() => handleDeletePressed()}
      />
    </PageContainer>
  );
};

export default ShowSlides;
